import { useState, useCallback, useMemo } from "react";
import {
  useScoringRules,
  useBulkUpdateScoringRules,
} from "@/hooks/useScoringRules";
import { useTagSuggestions } from "@/hooks/useTagSuggestions";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  buildScoringCSVFilename,
  rulesToCSVRows,
  serializeScoringCSV,
  triggerCSVDownload,
} from "@/lib/scoringCsv";
import { PreviewPanel } from "./PreviewPanel";
import { ScoringImportDialog } from "./ScoringImportDialog";
import { ScoringEditorHeader } from "./ScoringEditorHeader";
import { ScoringRulesList } from "./ScoringRulesList";
import { EmptyState } from "./EmptyState";
import {
  aggregateDrafts,
  createDraftFromRule,
  createEmptyDraft,
  splitAggregates,
  type DirtyMap,
  type DraftRule,
  type DraftRuleAggregate,
} from "./types";
import type { QuizQuestion } from "@/types/quiz";
import type { ScoringRuleInput } from "@/types/scoring-rules";
import type { BulkUpdateGroup } from "@/services/scoring-rules";

interface ScoringEditorProps {
  quizId: string;
  questions: QuizQuestion[];
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}

function StickySaveBar({
  changeCount,
  isSaving,
  onSave,
  onDiscard,
}: {
  changeCount: number;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  // Rendered unconditionally so its ~77px never enter or leave the flow (F-60).
  // When there is nothing to save the bar keeps its space but goes fully
  // inert: `invisible` also removes its buttons from the tab order, which
  // `opacity-0` would not.
  const isActive = changeCount > 0;
  return (
    <div
      className={cn(
        "sticky bottom-0 z-10 flex min-h-[var(--scoring-save-bar-height)] items-center justify-between border-t bg-background px-4 py-3 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]",
        !isActive && "invisible pointer-events-none",
      )}
      aria-hidden={!isActive}
    >
      <span className="text-sm text-muted-foreground">
        {changeCount} {changeCount === 1 ? "Änderung" : "Änderungen"}
      </span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDiscard}
          disabled={isSaving}
        >
          Verwerfen
        </Button>
        <Button type="button" size="sm" onClick={onSave} disabled={isSaving}>
          {isSaving ? "Speichert..." : "Speichern"}
        </Button>
      </div>
    </div>
  );
}

export function ScoringEditor({ quizId, questions }: ScoringEditorProps) {
  const { data, isLoading, isError, error } = useScoringRules(quizId);
  const bulkMutation = useBulkUpdateScoringRules(quizId);
  const { tags: availableTags } = useTagSuggestions(quizId);

  const [openItems, setOpenItems] = useState<string[]>([]);
  const [dirtyMap, setDirtyMap] = useState<DirtyMap>({});
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const handleImportComplete = useCallback((count: number) => {
    setDirtyMap({});
    setImportDialogOpen(false);
    setSaveSuccess(`${count} Scoring-Regeln importiert`);
    setTimeout(() => setSaveSuccess(null), 5000);
  }, []);

  const rules = useMemo(() => data?.rules ?? [], [data?.rules]);

  const handleExportCSV = useCallback(() => {
    const csvRows = rulesToCSVRows(rules, questions);
    const csv = serializeScoringCSV(csvRows);
    triggerCSVDownload(csv, buildScoringCSVFilename(quizId));
  }, [rules, questions, quizId]);

  const totalRules = data?.totalRules ?? 0;

  function getDraftsForAnswer(answerId: string): DraftRule[] {
    if (dirtyMap[answerId] !== undefined) {
      return dirtyMap[answerId];
    }
    const existing = rules.filter((r) => r.answerId === answerId);
    return existing.map(createDraftFromRule);
  }

  function isDirtyForAnswer(answerId: string): boolean {
    return dirtyMap[answerId] !== undefined;
  }

  const setDrafts = useCallback((answerId: string, drafts: DraftRule[]) => {
    setDirtyMap((prev) => ({ ...prev, [answerId]: drafts }));
  }, []);

  function updateAggregate(
    answerId: string,
    aggregateId: string,
    updated: DraftRuleAggregate,
  ) {
    const currentAggregates = aggregateDrafts(getDraftsForAnswer(answerId));
    const nextAggregates = currentAggregates.map((agg) =>
      agg.aggregateId === aggregateId ? updated : agg,
    );
    setDrafts(answerId, splitAggregates(nextAggregates));
  }

  function removeAggregate(answerId: string, aggregateId: string) {
    const currentAggregates = aggregateDrafts(getDraftsForAnswer(answerId));
    const nextAggregates = currentAggregates.filter(
      (agg) => agg.aggregateId !== aggregateId,
    );
    setDrafts(answerId, splitAggregates(nextAggregates));
  }

  function addDraft(answerId: string) {
    const current = getDraftsForAnswer(answerId);
    setDrafts(answerId, [...current, createEmptyDraft()]);
  }

  function findQuestionIdForAnswer(answerId: string): string | undefined {
    for (const q of questions) {
      const opt = q.options?.find((o) => o.id === answerId);
      if (opt) return q.questionKey;
    }
    return undefined;
  }

  function handleAddRule() {
    const firstWithOptions = questions.find((q) => (q.options ?? []).length > 0);
    if (!firstWithOptions) return;
    setOpenItems((prev) =>
      prev.includes(firstWithOptions.questionKey)
        ? prev
        : [...prev, firstWithOptions.questionKey],
    );
    addDraft(firstWithOptions.options![0].id);
  }

  const dirtyAnswerIds = Object.keys(dirtyMap);
  const changeCount = dirtyAnswerIds.length;

  function discardAll() {
    setDirtyMap({});
  }

  async function saveAll() {
    setSaveSuccess(null);
    const updates: BulkUpdateGroup[] = [];
    for (const [answerId, drafts] of Object.entries(dirtyMap)) {
      const existingRule = rules.find((r) => r.answerId === answerId);
      const questionId =
        existingRule?.questionId ?? findQuestionIdForAnswer(answerId);
      if (!questionId) continue;

      const ruleInputs: ScoringRuleInput[] = drafts
        .filter((d) => d.targetValue !== "")
        .map((d, i) => ({
          targetType: "tag" as const,
          targetValue: d.targetValue,
          weight: Math.max(-2, Math.min(2, Math.round(d.weight))),
          reasonText: d.reasonText.trim() || null,
          sortOrder: i,
          active: d.active,
        }));

      updates.push({ answerId, questionId, rules: ruleInputs });
    }

    if (updates.length === 0) return;

    await bulkMutation.mutateAsync(updates);
    setDirtyMap({});
    setSaveSuccess("Regeln gespeichert");
    setTimeout(() => setSaveSuccess(null), 3000);
  }

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-700">
          Scoring-Regeln konnten nicht geladen werden:{" "}
          {error instanceof Error ? error.message : "Unbekannter Fehler"}
        </p>
      </div>
    );
  }

  const hasRules = totalRules > 0 || dirtyAnswerIds.length > 0;
  const errorMessage =
    bulkMutation.isError
      ? bulkMutation.error instanceof Error
        ? bulkMutation.error.message
        : "Speichern fehlgeschlagen"
      : null;

  return (
    // The padding reserves the sticky bar's own height at the end of the
    // container, so the bar can stay pinned to the viewport edge all the way
    // down instead of detaching early, and content never ends beneath it.
    <div className="space-y-4 pb-[var(--scoring-save-bar-height)]">
      <ScoringEditorHeader
        ruleCount={totalRules}
        onAddRule={handleAddRule}
        onOpenImport={() => setImportDialogOpen(true)}
        onOpenExport={handleExportCSV}
        statusMessage={saveSuccess}
        errorMessage={errorMessage}
      />

      {availableTags.length === 0 && (
        <div className="rounded-lg border border-dashed border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
          Keine Produkt-Tags vorhanden. Bitte zuerst Tags in der
          Produktverwaltung anlegen.
        </div>
      )}

      {hasRules ? (
        <ScoringRulesList
          questions={questions}
          availableTags={availableTags}
          openItems={openItems}
          onOpenItemsChange={setOpenItems}
          getDraftsForAnswer={getDraftsForAnswer}
          isDirtyForAnswer={isDirtyForAnswer}
          updateAggregate={updateAggregate}
          removeAggregate={removeAggregate}
          addDraft={addDraft}
          quizId={quizId}
          rules={rules}
        />
      ) : (
        <EmptyState
          onAddRule={handleAddRule}
          onOpenImport={() => setImportDialogOpen(true)}
        />
      )}

      <PreviewPanel quizId={quizId} questions={questions} />

      <StickySaveBar
        changeCount={changeCount}
        isSaving={bulkMutation.isPending}
        onSave={saveAll}
        onDiscard={discardAll}
      />

      <ScoringImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={handleImportComplete}
        quizId={quizId}
        questions={questions}
        existingRules={rules}
      />
    </div>
  );
}
